import React from 'react';
import { Copy } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { cn } from '../lib/utils';

/**
 * SidePanel — tiroir coulissant depuis la droite.
 * - default: 480px sur desktop, plein écran sur mobile
 * - lg: 50vw sur desktop
 * - Overlay semi-transparent (liste principale toujours visible)
 * - À utiliser pour TOUS les formulaires de saisie
 * - Les confirmations (suppression, etc.) restent en Dialog/Modal centrée
 */
export function SidePanel({ open, onOpenChange, title, subtitle, children, className, size = 'default', width, footer, noPadding = false, headerExtra, headerSub, onPointerDownOutside, onFocusOutside, headerCenter, copyValue, stackedBehind = false }) {
  const computedMaxWidth = width
    ? `${width}px`
    : size === 'xl' ? '1344px' : size === 'lg' ? '55vw' : size === '60vw' ? '60vw' : size === 'plan' ? '624px' : '480px';
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'w-full p-0 flex flex-col gap-0 bg-white border-l',
          'shadow-[-6px_0_32px_rgba(0,0,0,0.08)]',
          className
        )}
        style={{
          borderColor: '#E6E6E1',
          maxWidth: computedMaxWidth,
          transition: 'transform 0.25s ease, filter 0.25s ease',
          transform: stackedBehind ? 'translateX(-28px) scale(0.98)' : 'none',
          filter: stackedBehind ? 'brightness(0.93)' : 'none',
        }}
        onPointerDownOutside={e => {
          // Empêche la fermeture si le clic vient d'un portal custom (ex: ZoneCombobox)
          const target = e.detail?.originalEvent?.target ?? e.target;
          if (target?.closest?.('[data-ignore-outside]')) { e.preventDefault(); return; }
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={e => {
          const target = e.detail?.originalEvent?.target ?? e.target;
          if (target?.closest?.('[data-ignore-outside]')) { e.preventDefault(); return; }
          onPointerDownOutside?.(e);
        }}
        onFocusOutside={e => {
          const target = e.detail?.originalEvent?.target ?? e.target;
          if (target?.closest?.('[data-ignore-outside]')) { e.preventDefault(); return; }
          onFocusOutside?.(e);
        }}
      >
        {/* Header fixe */}
        <SheetHeader
          className="flex-shrink-0 border-b"
          style={{ borderColor: '#E6E6E1', background: '#FAFAF8' }}
        >
          {/* Ligne 1 : titre | centre (smart buttons) | extra | [X] */}
          {headerCenter ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                gap: 8,
                paddingLeft: 24,
                paddingRight: 56,
                height: 52,
              }}
            >
              <div className="flex items-center gap-1.5">
                <SheetTitle
                  className="text-base font-bold whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ color: 'var(--primary-solid)',  }}
                >
                  {title}
                </SheetTitle>
                {copyValue && (
                  <button
                    onClick={() => navigator.clipboard?.writeText(copyValue)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', lineHeight: 0 }}
                  >
                    <Copy size={13} />
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
                {headerCenter}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {headerExtra}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between px-6 pr-14" style={{ height: '52px' }}>
              <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0">
                <SheetTitle
                  className="text-base font-bold whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ color: 'var(--primary-solid)',  }}
                >
                  {title}
                </SheetTitle>
                {copyValue && (
                  <button
                    onClick={() => navigator.clipboard?.writeText(copyValue)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', lineHeight: 0 }}
                  >
                    <Copy size={13} />
                  </button>
                )}
              </div>
              {headerExtra && (
                <div className="flex items-center flex-1 min-w-0 ml-4 justify-end">
                  {headerExtra}
                </div>
              )}
            </div>
          )}
          {/* Ligne 2 (optionnelle) : stepper ou autre sous-titre */}
          {headerSub && (
            <div className="px-6 pb-3 pt-2 border-t flex items-center" style={{ borderColor: '#E6E6E1' }}>
              {headerSub}
            </div>
          )}
        </SheetHeader>
        <SheetDescription className="sr-only">
          {subtitle || title}
        </SheetDescription>

        {/* Corps */}
        <div
          className={cn('flex-1 min-h-0', noPadding ? 'overflow-hidden' : 'overflow-y-auto px-6 py-5')}
          style={{ background: '#FFFFFF' }}
        >
          {children}
        </div>

        {/* Footer fixe (optionnel) */}
        {footer && (
          <div className="flex-shrink-0 px-6 py-4 border-t" style={{ borderColor: '#E6E6E1', background: '#FAFAF8', position: 'relative', zIndex: 10 }}>
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
